import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpStatusCode } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Signer } from '@aws-amplify/core/internals/utils'
import { catchError, Observable, throwError } from 'rxjs'
import { environment } from '../../environments/environment'
import { AuthSession } from '../model/app.model'
import { AuthStateService } from '../core/auth/auth-state.service'
import { AuthService } from '../core/auth/auth.service'
import { sha256 } from 'js-sha256'

const API_URL_EXCLUSIONS = ['/assets', 'assets']

// Matches regional (bucket.s3.eu-north-1.amazonaws.com, s3.eu-north-1.amazonaws.com/bucket)
// and global (bucket.s3.amazonaws.com) endpoints — the region groups stay empty for global ones
export const S3_HOST_REGEX = /\.s3[.-]([a-z0-9-]+)\.amazonaws\.com|\bs3\.(?:([a-z0-9-]+)\.)?amazonaws\.com/i

// Null for non-S3 urls and for global endpoints, which carry no region in the host
export function parseS3Region(url: string): string | null {
	const match = S3_HOST_REGEX.exec(url)
	return match ? match[1] || match[2] || null : null
}

const HEADER_NAMES = {
	authorization: 'Authorization',
	cognitoToken: 'CognitoToken',
	xAmzSecurityToken: 'X-Amz-Security-Token',
	xAmzDate: 'x-amz-date',
	xHostOverride: 'X-Host-Override',
	xAmzContentSha: 'x-amz-content-sha256',
}

const EXPIRED_TOKEN_MESSAGES = ['The security token included in the request is expired', 'token is expired']

type HeadersType = Record<string, string | string[]>

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private readonly authState = inject(AuthStateService)
	private readonly authService = inject(AuthService)

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const session = this.authState.session()

		if (!this.authState.isAuthenticated() || !session) {
			this.authService.federatedSignIn()
			return throwError(() => new Error('not authenticated'))
		}

		const s3Region = parseS3Region(req.url)

		let signedReq: HttpRequest<unknown>
		if (s3Region) {
			signedReq = this.signS3Request(session, req, s3Region)
		} else if (this.shouldPresignUrl(req)) {
			// SigV4 in query params instead of headers — the browser-followed 302 to S3
			// can't carry x-amz-* request headers that S3 rejects as unsigned.
			signedReq = this.presignApiRequest(session, req)
		} else {
			signedReq = this.signAndCombineRequest(session, req.clone({ setHeaders: this.createStaticHeaders(session) }))
		}

		return next.handle(signedReq).pipe(
			catchError(error => {
				if (error instanceof HttpErrorResponse) {
					if (error.status === HttpStatusCode.Unauthorized || (error.error?.message && EXPIRED_TOKEN_MESSAGES.some(m => error.error.message.includes(m)))) {
						this.authService.federatedSignIn()
					}
				}
				return throwError(() => error)
			}),
		)
	}

	private signS3Request(session: AuthSession, req: HttpRequest<unknown>, region: string): HttpRequest<unknown> {
		const headers: HeadersType = {}
		req.headers.keys().forEach(key => {
			const value = req.headers.get(key)
			if (value) headers[key] = value
		})

		const EMPTY_BODY_SHA256 = sha256('')
		headers[HEADER_NAMES.xAmzContentSha] = EMPTY_BODY_SHA256

		const rawRequest = {
			method: req.method,
			url: req.urlWithParams,
			headers,
			data: req.serializeBody(),
		}

		const accessInfo = {
			access_key: session.credentials.accessKeyId,
			secret_key: session.credentials.secretAccessKey,
			session_token: session.credentials.sessionToken,
		}

		const signedRaw = Signer.sign(rawRequest, accessInfo, { region, service: 's3' })

		return req.clone({
			setHeaders: {
				[HEADER_NAMES.authorization]: signedRaw.headers[HEADER_NAMES.authorization],
				[HEADER_NAMES.xAmzSecurityToken]: signedRaw.headers[HEADER_NAMES.xAmzSecurityToken],
				[HEADER_NAMES.xAmzDate]: signedRaw.headers[HEADER_NAMES.xAmzDate],
				[HEADER_NAMES.xAmzContentSha]: EMPTY_BODY_SHA256,
			},
		})
	}

	private shouldPresignUrl(req: HttpRequest<unknown>): boolean {
		// Only atom downloads — the content-items CSV export also 302s to S3, but its
		// backend parses every query param as a filter field and rejects the X-Amz-*
		// params that presigning appends ("Unsupported filter field: X-Amz-Algorithm")
		return req.method === 'GET' && /\/atoms\/[^/]+$/.test(req.url)
	}

	private presignApiRequest(session: AuthSession, req: HttpRequest<unknown>): HttpRequest<unknown> {
		const accessInfo = {
			access_key: session.credentials.accessKeyId,
			secret_key: session.credentials.secretAccessKey,
			session_token: session.credentials.sessionToken,
		}

		const serviceInfo = {
			region: environment.awsCognitoConfig.region,
			service: 'execute-api',
		}

		const presignedUrl = Signer.signUrl(this.resolveFullApiUrl(req.urlWithParams), accessInfo, serviceInfo)

		// The original params are already baked into the presigned url — clear them,
		// otherwise clone() re-appends them after the signature and invalidates it
		return req.clone({ url: presignedUrl, params: new HttpParams() })
	}

	private signAndCombineRequest(session: AuthSession, req: HttpRequest<unknown>): HttpRequest<unknown> {
		const rawRequest = this.createRawRequest(req)

		const accessInfo = {
			access_key: session.credentials.accessKeyId,
			secret_key: session.credentials.secretAccessKey,
			session_token: session.credentials.sessionToken,
		}

		const serviceInfo = {
			region: environment.awsCognitoConfig.region,
			service: 'execute-api',
		}

		const signedRaw = Signer.sign(rawRequest, accessInfo, serviceInfo)

		const headers: HeadersType = {
			[HEADER_NAMES.authorization]: signedRaw.headers[HEADER_NAMES.authorization],
			[HEADER_NAMES.cognitoToken]: signedRaw.headers[HEADER_NAMES.cognitoToken],
			[HEADER_NAMES.xAmzSecurityToken]: signedRaw.headers[HEADER_NAMES.xAmzSecurityToken],
			[HEADER_NAMES.xAmzDate]: signedRaw.headers[HEADER_NAMES.xAmzDate],
		}

		if (environment.awsCognitoConfig.headerXHostOverride) {
			headers[HEADER_NAMES.xHostOverride] = signedRaw.headers['host']
		}

		return req.clone({
			url: environment.useProxy ? req.url : this.resolveFullApiUrl(req.url),
			setHeaders: headers,
		})
	}

	private createRawRequest(req: HttpRequest<unknown>) {
		const headers: HeadersType = {}
		req.headers.keys().forEach(key => {
			const value = req.headers.get(key)
			if (value) headers[key] = value
		})

		return {
			method: req.method,
			url: this.resolveFullApiUrl(req.urlWithParams),
			headers,
			data: req.serializeBody(),
		}
	}

	private createStaticHeaders(session: AuthSession): HeadersType {
		return { [HEADER_NAMES.cognitoToken]: session.user.idToken.jwtToken }
	}

	private resolveFullApiUrl(uri: string): string {
		const isApiUrl = !API_URL_EXCLUSIONS.find(p => uri.indexOf(p, 0) === 0)
		if (isApiUrl) {
			uri = uri.replace('/api/', `${environment.apiUrl}/`)
		}
		return uri
	}
}
